// src/components/SR_SignaturePad.jsx
import React, { useEffect, useRef, useState } from "react";

export default function SR_SignaturePad({
  width = 600,
  height = 160,
  penColor = "#000000",
  backgroundColor = "#ffffff",
  lineWidth = 2.0,
  onChange,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // DPR scaling for crisp lines
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Paint solid background (white by default) so the PNG is opaque
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Border (subtle)
    ctx.strokeStyle = "#444";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
  }, [width, height, backgroundColor]);

  function posFromEvent(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    drawing.current = true;
    last.current = posFromEvent(e);
  }

  function move(e) {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = posFromEvent(e);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = penColor; // solid black by default
    ctx.lineWidth = lineWidth;

    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setIsEmpty(false);

    if (onChange) {
      // Emit a PNG data URL (opaque because we pre-painted background)
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  }

  function end() {
    drawing.current = false;
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
    ctx.strokeStyle = "#444";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, canvas.width / (window.devicePixelRatio || 1) - 1, canvas.height / (window.devicePixelRatio || 1) - 1);
    setIsEmpty(true);
    if (onChange) onChange(null);
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <canvas
        ref={canvasRef}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        style={{ background: backgroundColor, borderRadius: 8, touchAction: "none" }}
      />
      <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
        <span style={{ color: "#8a8a95", fontSize: 12 }}>
          {isEmpty ? "Sign above" : "Tap Clear to re-sign"}
        </span>
        <button type="button" className="btn" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
}